/**
 * PhaseClosurePanel
 * Cierre formal de las 6 fases del Plan de Validacion (VP-ECRD-001)
 * con firma electronica 21 CFR Part 11 (PIN + motivo + audit ALCOA+).
 *
 * Endpoints:
 *   GET  /api/validation/phases
 *   POST /api/validation/phases/{phase_id}/close   { pin, reason }
 *   POST /api/validation/phases/{phase_id}/reopen  { pin, reason }
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { CheckCircle, LockKey, LockKeyOpen, FingerprintSimple, Warning } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PHASE_LABELS = {
  es: {
    phase_1: { title: 'Fase 1 - URS + Plan de Validacion', role: 'Responsable de Validacion',
      hint: 'Se cierra cuando los documentos URS y VP estan generados, revisados y aprobados.' },
    phase_2: { title: 'Fase 2 - Cualificacion de Instalacion (IQ)', role: 'Director Tecnico / IT',
      hint: 'Se cierra cuando el sistema esta correctamente instalado en el entorno productivo (verificado stack, despliegue, backups).' },
    phase_3: { title: 'Fase 3 - Cualificacion Operacional (OQ)', role: 'Responsable de Validacion / QA',
      hint: 'Se cierra cuando las pruebas automatizadas cubren todos los URS funcionales con tasa de exito objetivo.' },
    phase_4: { title: 'Fase 4 - Cualificacion de Rendimiento (PQ)', role: 'Director de Calidad',
      hint: 'Se cierra cuando hay evidencia de uso real estable (volumen de datos, firmas, audit, sin incidencias criticas). Genera previamente el PQ-Report-ECRD-001.' },
    phase_5: { title: 'Fase 5 - Informe Final de Validacion', role: 'Director de Calidad',
      hint: 'Se cierra firmando el VR-ECRD-001. Solo deberia firmarse cuando las Fases 1-4 estan cerradas.' },
    phase_6: { title: 'Fase 6 - Aprobacion y Puesta en Produccion', role: 'Direccion General / Patrocinador',
      hint: 'Se cierra automaticamente despues de la Fase 5 al confirmar el regimen de monitorizacion continua + control de cambios.' },
  },
  en: {
    phase_1: { title: 'Phase 1 - URS + Validation Plan', role: 'Validation Manager', hint: '' },
    phase_2: { title: 'Phase 2 - Installation Qualification (IQ)', role: 'Tech Director / IT', hint: '' },
    phase_3: { title: 'Phase 3 - Operational Qualification (OQ)', role: 'Validation Manager / QA', hint: '' },
    phase_4: { title: 'Phase 4 - Performance Qualification (PQ)', role: 'Quality Director', hint: '' },
    phase_5: { title: 'Phase 5 - Final Validation Report', role: 'Quality Director', hint: '' },
    phase_6: { title: 'Phase 6 - Approval & Production', role: 'General Direction / Sponsor', hint: '' },
  },
};

const PHASE_ORDER = ['phase_1', 'phase_2', 'phase_3', 'phase_4', 'phase_5', 'phase_6'];

function fmtDate(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export function PhaseClosurePanel({ lang }) {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signDialog, setSignDialog] = useState(null); // { phase, mode: 'close'|'reopen' }
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const t = (k) => (PHASE_LABELS[lang === 'en' ? 'en' : 'es'][k] || {});

  const fetchPhases = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/validation/phases`, { withCredentials: true });
      // Ensure consistent order
      const map = Object.fromEntries((data.phases || []).map(p => [p.phase_id, p]));
      setPhases(PHASE_ORDER.map(pid => map[pid]).filter(Boolean));
    } catch (err) {
      toast.error(errText(err, 'Error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPhases(); }, []);

  const openSign = (phase, mode) => {
    setSignDialog({ phase, mode });
    setPin('');
    setReason('');
  };

  const submitSign = async () => {
    if (!pin || !reason.trim()) {
      toast.error(lang === 'es' ? 'PIN y motivo obligatorios' : 'PIN and reason required');
      return;
    }
    setSubmitting(true);
    try {
      const url = `${API_URL}/api/validation/phases/${signDialog.phase.phase_id}/${signDialog.mode}`;
      await axios.post(url, { pin, reason: reason.trim() }, { withCredentials: true });
      toast.success(
        signDialog.mode === 'close'
          ? (lang === 'es' ? 'Fase cerrada electronicamente' : 'Phase closed electronically')
          : (lang === 'es' ? 'Fase reabierta (control de cambios)' : 'Phase reopened (change control)')
      );
      setSignDialog(null);
      await fetchPhases();
    } catch (err) {
      toast.error(errText(err, 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  const total = phases.length;
  const closed = phases.filter(p => p.status === 'closed').length;
  const progressPct = total > 0 ? Math.round((closed / total) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="phase-closure-panel">
      <div className="bg-[#FFF8E7] border border-[#B8860B]/30 rounded-sm p-4">
        <div className="flex items-center justify-between gap-4 mb-2">
          <div>
            <p className="text-sm text-[#7A5F00] font-semibold flex items-center gap-2">
              <FingerprintSimple size={16} weight="bold" />
              {lang === 'es' ? 'Cierre Formal de Fases del Plan de Validacion' : 'Formal Closure of Validation Plan Phases'}
            </p>
            <p className="text-xs text-[#7A5F00]/80 mt-1">
              {lang === 'es'
                ? 'Cada fase se cierra con firma electronica 21 CFR Part 11 (PIN + motivo). Las firmas quedan registradas en audit_logs ALCOA+ y se embeben en los PDFs VP/PQ/VR.'
                : 'Each phase is closed with 21 CFR Part 11 electronic signature (PIN + reason). Signatures are recorded in ALCOA+ audit_logs and embedded in VP/PQ/VR PDFs.'}
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl font-bold text-[#7A5F00]">{closed}/{total}</div>
            <div className="text-xs text-[#7A5F00]/70">{lang === 'es' ? 'fases cerradas' : 'phases closed'}</div>
          </div>
        </div>
        <div className="w-full bg-[#B8860B]/10 rounded-full h-1.5 overflow-hidden">
          <div className="h-1.5 bg-[#B8860B] transition-all" style={{ width: `${progressPct}%` }} data-testid="phase-progress-bar" />
        </div>
      </div>

      {loading && (
        <div className="text-sm text-stone-500 py-4 text-center">
          {lang === 'es' ? 'Cargando fases...' : 'Loading phases...'}
        </div>
      )}

      {!loading && phases.map((p, idx) => {
        const meta = t(p.phase_id);
        const isClosed = p.status === 'closed';
        const prevClosed = idx === 0 || phases[idx - 1]?.status === 'closed';
        const orderWarning = !isClosed && !prevClosed;
        return (
          <div
            key={p.phase_id}
            data-testid={`phase-card-${p.phase_id}`}
            className={`bg-white border rounded-sm p-4 flex items-start gap-4 ${isClosed ? 'border-emerald-300 bg-emerald-50/30' : 'border-stone-200'}`}
          >
            <div className="mt-0.5 shrink-0">
              {isClosed ? (
                <CheckCircle size={28} weight="fill" className="text-emerald-600" />
              ) : (
                <LockKey size={28} weight="bold" className="text-stone-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-sm font-semibold text-stone-900">{meta.title || p.label_es}</span>
                {isClosed ? (
                  <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px]" data-testid={`phase-badge-closed-${p.phase_id}`}>
                    {lang === 'es' ? 'CERRADA' : 'CLOSED'}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-stone-500 text-[10px]" data-testid={`phase-badge-open-${p.phase_id}`}>
                    {lang === 'es' ? 'ABIERTA' : 'OPEN'}
                  </Badge>
                )}
                {orderWarning && (
                  <span className="text-[10px] text-amber-700 flex items-center gap-1">
                    <Warning size={11} weight="fill" />
                    {lang === 'es' ? 'Sugerencia: cierra antes la fase anterior' : 'Hint: close previous phase first'}
                  </span>
                )}
              </div>
              <p className="text-xs text-stone-500 leading-relaxed mb-2">
                <span className="font-medium">{lang === 'es' ? 'Rol firmante: ' : 'Signer role: '}</span>
                {meta.role || p.role_required_es}
              </p>
              {meta.hint && !isClosed && (
                <p className="text-xs text-[#1f3e80] italic leading-relaxed mb-2 bg-[#1f3e80]/5 px-2 py-1.5 rounded">
                  {meta.hint}
                </p>
              )}
              {isClosed && (
                <div className="mt-2 text-xs text-stone-700 bg-emerald-100/40 border border-emerald-200 rounded-sm p-2 space-y-0.5">
                  <div><span className="font-medium">{lang === 'es' ? 'Firmante:' : 'Signer:'}</span> {p.signer_name || '-'} ({p.signer_email || '-'})</div>
                  <div><span className="font-medium">{lang === 'es' ? 'Fecha:' : 'Date:'}</span> {fmtDate(p.closed_at)}</div>
                  <div><span className="font-medium">{lang === 'es' ? 'Motivo:' : 'Reason:'}</span> {p.reason || '-'}</div>
                  <div className="font-mono text-[10px] text-stone-500">audit_id: {p.audit_id || '-'}</div>
                </div>
              )}
            </div>
            <div className="shrink-0">
              {isClosed ? (
                <Button
                  variant="outline"
                  className="rounded-sm gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
                  onClick={() => openSign(p, 'reopen')}
                  data-testid={`phase-reopen-btn-${p.phase_id}`}
                >
                  <LockKeyOpen size={14} />
                  {lang === 'es' ? 'Reabrir' : 'Reopen'}
                </Button>
              ) : (
                <Button
                  className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-1.5"
                  onClick={() => openSign(p, 'close')}
                  data-testid={`phase-close-btn-${p.phase_id}`}
                >
                  <FingerprintSimple size={14} />
                  {lang === 'es' ? 'Cerrar y firmar' : 'Close and sign'}
                </Button>
              )}
            </div>
          </div>
        );
      })}

      <Dialog open={!!signDialog} onOpenChange={(o) => { if (!o) setSignDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="phase-sign-dialog">
          <DialogHeader>
            <DialogTitle>
              {signDialog?.mode === 'close'
                ? (lang === 'es' ? 'Firma electronica de cierre de fase' : 'Phase closure e-signature')
                : (lang === 'es' ? 'Reapertura de fase (control de cambios)' : 'Phase reopen (change control)')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-sm p-2">
              <div className="font-medium">{t(signDialog?.phase?.phase_id)?.title || signDialog?.phase?.label_es}</div>
              <div className="mt-0.5 text-stone-500">
                {lang === 'es' ? 'Rol firmante esperado: ' : 'Expected signer role: '}
                {t(signDialog?.phase?.phase_id)?.role || signDialog?.phase?.role_required_es}
              </div>
            </div>
            <div>
              <Label htmlFor="phase-pin">{lang === 'es' ? 'PIN de firma' : 'Signature PIN'}</Label>
              <Input
                id="phase-pin"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="****"
                className="mt-1"
                data-testid="phase-sign-pin-input"
              />
            </div>
            <div>
              <Label htmlFor="phase-reason">{lang === 'es' ? 'Motivo' : 'Reason'}</Label>
              <Textarea
                id="phase-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder={signDialog?.mode === 'close'
                  ? (lang === 'es' ? 'Ej: Fase 4 PQ cualificada: 7900+ voluntarios reales, 6 meses uso productivo sin incidencias criticas.' : 'e.g.: Phase 4 PQ qualified')
                  : (lang === 'es' ? 'Motivo de reapertura (cambio controlado obligatorio)' : 'Reopen reason (mandatory change control)')}
                className="mt-1"
                data-testid="phase-sign-reason-input"
              />
            </div>
            {signDialog?.mode === 'reopen' && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 flex items-start gap-1.5">
                <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
                <span>
                  {lang === 'es'
                    ? 'Reabrir una fase invalida los documentos firmados que dependian de ella. Quedara registrada en audit_logs como PHASE_REOPEN.'
                    : 'Reopening a phase invalidates signed documents that depended on it. It will be logged as PHASE_REOPEN.'}
                </span>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignDialog(null)} disabled={submitting}>
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              onClick={submitSign}
              disabled={submitting || !pin || !reason.trim()}
              className={signDialog?.mode === 'close' ? 'bg-[#1f3e80] hover:bg-[#2a4f8a] text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
              data-testid="phase-sign-confirm-btn"
            >
              <FingerprintSimple size={14} className="mr-1.5" />
              {submitting
                ? (lang === 'es' ? 'Firmando...' : 'Signing...')
                : (signDialog?.mode === 'close'
                    ? (lang === 'es' ? 'Firmar y cerrar' : 'Sign and close')
                    : (lang === 'es' ? 'Firmar y reabrir' : 'Sign and reopen'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
